import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { EpreuvesList2 } from "./EpreuvesList2";
import { loadEpreuves } from "./requests";
import Select from "react-select";
import MapLocation from "./Components/MapLocate";
import makeAnimated from "react-select/animated";
import ProfileSelector from "./Components/ProfileSelector";
import CancelPresentationIcon from "@material-ui/icons/CancelPresentation";
import SearchIcon from "@material-ui/icons/Search";
import Loader from "./medias/loader.gif";

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1; // monthActive : 1 = Janvier ... 12 = Décembre
const animatedComponents = makeAnimated();

const SEARCH_DEBOUNCE_MS = 400;

// ---- Données statiques (hors composant : pas recréées à chaque rendu) ----

const checkboxes = [
  { key: 0, name: "Jeunes", label: "Jeunes" },
  { key: 1, name: "XXS", label: "XXS" },
  { key: 2, name: "XS", label: "XS" },
  { key: 3, name: "S", label: "S" },
  { key: 4, name: "M", label: "M" },
  { key: 5, name: "L", label: "L" },
  { key: 6, name: "XL", label: "XL" },
  { key: 7, name: "XXL", label: "XXL" },
];

const yearActive = [
  { value: 2022, label: "2022" },
  { value: 2023, label: "2023" },
  { value: 2024, label: "2024" },
  { value: 2025, label: "2025" },
  { value: 2026, label: "2026" },
  { value: 2027, label: "2027" },
];

const monthActive = [
  { value: 0, label: "Toute l'année" },
  { value: 1, label: "Janvier" },
  { value: 2, label: "Février" },
  { value: 3, label: "Mars" },
  { value: 4, label: "Avril" },
  { value: 5, label: "Mai" },
  { value: 6, label: "Juin" },
  { value: 7, label: "Juillet" },
  { value: 8, label: "Août" },
  { value: 9, label: "Septembre" },
  { value: 10, label: "Octobre" },
  { value: 11, label: "Novembre" },
  { value: 12, label: "Décembre" },
];

const options2 = [
  { value: "Toute la France", label: "Toute la France" },
  { value: "Alsace", label: "Alsace" },
  { value: "Aquitaine", label: "Aquitaine" },
  { value: "Auvergne", label: "Auvergne" },
  { value: "Bretagne", label: "Bretagne" },
  { value: "Bourgogne", label: "Bourgogne" },
  { value: "Champagne-Ardenne", label: "Champagne-Ardenne" },
  { value: "Centre", label: "Centre" },
  { value: "Corse", label: "Corse" },
  { value: "Franche-Comté", label: "Franche-Comté" },
  { value: "Guadeloupe", label: "Guadeloupe" },
  { value: "Guyane", label: "Guyane" },
  { value: "Ile-de-France", label: "Ile-de-France" },
  { value: "Languedoc-Roussillon", label: "Languedoc-Roussillon" },
  { value: "La-Réunion", label: "La-Réunion" },
  { value: "Limousin", label: "Limousin" },
  { value: "Lorraine", label: "Lorraine" },
  { value: "Mayotte", label: "Mayotte" },
  { value: "Martinique", label: "Martinique" },
  { value: "Midi-Pyrénées", label: "Midi-Pyrénées" },
  { value: "Nord-Pas-de-Calais", label: "Nord-Pas-de-Calais" },
  { value: "Normandie", label: "Normandie" },
  { value: "Nouvelle-Calédonie", label: "Nouvelle-Calédonie" },
  { value: "Pays-de-la-Loire", label: "Pays-de-la-Loire" },
  { value: "Picardie", label: "Picardie" },
  { value: "Poitou-Charentes", label: "Poitou-Charentes" },
  { value: "Provence-Alpes-Côte-d'Azur", label: "Provence-Alpes-Côte-d'Azur" },
  { value: "Rhône-Alpes", label: "Rhône-Alpes" },
];

const DEFAULT_CHECKED_ITEMS = {
  Jeunes: true,
  XXS: true,
  XS: true,
  S: true,
  M: true,
  L: true,
  XL: true,
  XXL: true,
};

// ---- Helpers localStorage (lecture/écriture centralisées) ----

const readStoredFilters = () => ({
  year: window.localStorage.getItem("year")
    ? Number(window.localStorage.getItem("year"))
    : currentYear,
  month: window.localStorage.getItem("month")
    ? Number(window.localStorage.getItem("month"))
    : currentMonth,
  region: window.localStorage.getItem("region") || "Toute la France",
});

export const EpreuveBoard = () => {
  const stored = useMemo(readStoredFilters, []);

  const [epreuves, setEpreuves] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState({
    value: stored.region,
    label: stored.region,
  });
  // Valeur brute de l'input (mise à jour à chaque frappe)
  const [searchInput, setSearchInput] = useState("");
  // Valeur "debounced" utilisée pour déclencher réellement la recherche
  const [nameEvent, setNameEvent] = useState("");
  const [yearSelected, setYearSelected] = useState({
    value: stored.year,
    label: stored.year.toString(),
  });
  const [monthSelected, setMonthSelected] = useState({
    value: stored.month,
    label: monthActive[stored.month].label,
  });
  const [checkedItems, setCheckedItems] = useState(DEFAULT_CHECKED_ITEMS);
  const [loader, setLoader] = useState(false);

  // Système de reload de la carte
  const [childKey, setChildKey] = useState(0);
  const reloadChild = () => setChildKey((prevKey) => prevKey + 1);

  // Protection contre les réponses de fetch qui arriveraient dans le désordre
  const latestRequestId = useRef(0);

  // ---- Handlers ----

  const handleChange = (event) => {
    setCheckedItems((prev) => ({
      ...prev,
      [event.target.name]: event.target.checked,
    }));
  };

  const handleRegion = (region) => setSelectedRegion(region);
  const handleMonth = (month) => setMonthSelected(month);
  const handleYear = (year) => {
    setYearSelected(year);
    reloadChild();
  };
  const handleSearch = (event) => setSearchInput(event.target.value);

  const removeFilters = () => {
    setSelectedRegion({ value: "Toute la France", label: "Toute la France" });
    setSearchInput("");
    setNameEvent("");
    setMonthSelected({ value: currentMonth, label: monthActive[currentMonth].label });
    window.localStorage.clear();
  };

  // ---- Debounce de la recherche texte : on évite un fetch par frappe ----
  useEffect(() => {
    const timeout = setTimeout(() => {
      setNameEvent(searchInput);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  // ---- Formats sélectionnés, dérivés de checkedItems (pas de state dupliqué) ----
  const selectedFormats = useMemo(
    () => Object.keys(checkedItems).filter((key) => checkedItems[key]),
    [checkedItems]
  );

  // ---- Persistance des filtres (un seul point d'écriture) ----
  useEffect(() => {
    window.localStorage.setItem("year", yearSelected.value);
    window.localStorage.setItem("month", monthSelected.value);
    window.localStorage.setItem("region", selectedRegion.value);
  }, [yearSelected, monthSelected, selectedRegion]);

  // ---- Récupération des données ----
  const fetchData = useCallback(async () => {
    const requestId = ++latestRequestId.current;
    setLoader(true);

    const region =
      selectedRegion.value === "Toute la France" ? "" : selectedRegion.value;

    try {
      const epreuvesResult = await loadEpreuves({
        region,
        name: nameEvent,
        month: monthSelected.value,
        year: yearSelected.value,
        type: selectedFormats,
        relay: "",
        Elevation_bike: 0,
        bibs: 25000,
        Swimenv: ["Piscine", "Lac", "Mer", "Rivière", "Plan d'eau", "0"],
        crosstri: ["1", "0"],
      });

      // On ignore la réponse si une requête plus récente a été lancée entre-temps
      if (requestId === latestRequestId.current) {
        setEpreuves(epreuvesResult);
      }
    } catch (error) {
      if (requestId === latestRequestId.current) {
        console.error("Erreur lors du chargement des épreuves :", error);
      }
    } finally {
      if (requestId === latestRequestId.current) {
        setLoader(false);
      }
    }
  }, [selectedRegion, nameEvent, monthSelected, yearSelected, selectedFormats]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="container my-2 visible-md">
      <ProfileSelector />
      {/* Only for smartphone : search engine */}
      <div className="col-md-6 p-0 input-group my-3">
        <span
          className="input-group-text"
          id="Search triathlon"
          style={{ borderRadius: "0px", height: "38px" }}
        >
          <SearchIcon alt="Rechercher une épreuve" />
        </span>
        <div className="input-group-prepend"></div>
        <input
          type="text"
          className="form-control"
          id="Search triathlon"
          aria-describedby="search triathlon by name"
          onChange={handleSearch}
          value={searchInput}
          placeholder="Rechercher une épreuve"
          style={{ height: "38px" }}
        />
        <span
          className="btn btn-warning ml-2 btn-sm"
          onClick={removeFilters}
          style={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}
        >
          <CancelPresentationIcon
            alt="Effacer la recherche"
            className="py-auto "
          />
        </span>
      </div>

      {/* En dessous de "lg" (smartphone/tablette) : les critères de recherche passent en premier.
          À partir de "lg" (desktop) : ordre d'origine (résultats à gauche, critères à droite). */}
      <div className="row">
        <div className="col-lg-8 order-2 order-lg-1">
          <div className="row mb-3">
            <h1 className="title">
              Calendrier des triathlons {loader ? null : yearSelected.value} en
              France
            </h1>
          </div>
          <div className="row my-2 font-weight-bold">
            <h6 className="font-weight-bold text-warning">
              {epreuves.length} épreuves (
              {selectedRegion.value +
                " - " +
                monthSelected.label +
                " " +
                yearSelected.value}
              )
            </h6>
          </div>
          {loader ? (
            <img
              src={Loader}
              alt="Chargement des résultats..."
              className="mx-auto d-block"
            />
          ) : (
            <EpreuvesList2
              epreuves={epreuves}
              remove={true}
              filterByRegion={selectedRegion ? selectedRegion.value : ""}
            />
          )}
        </div>
        {/* Right column */}
        <div className="col-lg-4 order-1 order-lg-2">
          <div className=" ml-md-2">
            <div className="col-lg-12 font-weight-bold mb-2 ">
              <h3 className="font-weight-bold">Rechercher un triathlon</h3>

              <div
                className="col-lg-12 p-0 mb-2"
                style={{ position: "relative", zIndex: "2" }}
              >
                <Select
                  components={animatedComponents}
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                  onChange={handleRegion}
                  options={options2}
                  value={selectedRegion}
                  placeholder="Toute la France"
                />
              </div>
              <div className="row">
                <div
                  className="col-lg-6 p-0 ml-md-3 mb-2"
                  style={{ position: "relative", zIndex: "2" }}
                >
                  <Select
                    onChange={handleMonth}
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    options={monthActive}
                    value={monthSelected}
                    placeholder="Choisir un mois"
                  />
                </div>
                <div className="col-lg-5 p-0 ml-md-1 mb-2">
                  <Select
                    onChange={handleYear}
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    options={yearActive}
                    value={yearSelected}
                    placeholder={currentYear}
                  />
                </div>
              </div>
              <div className="row">
                <div
                  className="col-lg-12 p-0 ml-3 mb-2 accentColor-primary"
                  style={{ position: "relative", zIndex: "2" }}
                >
                  <label className="border-bottom border-primary">
                    Sélectionner le format&nbsp;:
                  </label>{" "}
                  <br />
                  {checkboxes.map((item) => (
                    <label key={item.key} className="mr-2">
                      {item.name}
                      <input
                        type="checkbox"
                        className="ml-2"
                        name={item.name}
                        checked={checkedItems[item.name]}
                        onChange={handleChange}
                      />
                    </label>
                  ))}
                </div>
              </div>
              {/* Map */}
              <div className="mt-1">
                <h4 className="text-warning">
                  Trouver votre triathlon sur la carte
                </h4>
                <MapLocation
                  zoomSize="5"
                  region={selectedRegion}
                  epreuves={epreuves}
                  yearSelected={yearSelected}
                  key={childKey}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
